import type { Spot } from "./types";
import { regionById } from "./regions";

export interface SpotSearchResult<T extends Spot = Spot> {
  spot: T;
  /** Plain-text reason this match surfaced — shown as the result subtitle. */
  matchedOn: string;
  /** Lower number = better match. */
  score: number;
}

/**
 * Prefix-based search across spot name, area, region label, subregion
 * (län), and the freeform note. Returns the top results sorted by
 * relevance. Empty query returns an empty list (callers should hide
 * the result list until the user types).
 *
 * Generic over the spot type so EnrichedSpot[] in → EnrichedSpot
 * results out (callers need access to .days for the per-row forecast
 * summary).
 */
export function searchSpots<T extends Spot>(
  query: string,
  spots: readonly T[],
): SpotSearchResult<T>[] {
  const q = query.trim().toLowerCase();
  if (!q) return [];

  const out: SpotSearchResult<T>[] = [];
  for (const spot of spots) {
    const name = spot.name.toLowerCase();
    const area = spot.area.toLowerCase();
    const sub = spot.subregion?.toLowerCase() ?? "";
    const region = regionById(spot.region)?.label.toLowerCase() ?? "";
    const note = (spot.note ?? "").toLowerCase();

    // Score: prefix matches beat substring matches; name beats area
    // beats region beats note. Lower score = better.
    let score = Number.POSITIVE_INFINITY;
    let matchedOn = "";

    if (name.startsWith(q)) {
      score = 0;
      matchedOn = spot.area;
    } else if (name.includes(q)) {
      score = 10;
      matchedOn = spot.area;
    } else if (area.startsWith(q)) {
      score = 20;
      matchedOn = spot.area;
    } else if (area.includes(q)) {
      score = 30;
      matchedOn = spot.area;
    } else if (sub.startsWith(q) || region.startsWith(q)) {
      score = 40;
      matchedOn = `${spot.area} · ${regionById(spot.region)?.label ?? ""}`;
    } else if (sub.includes(q) || region.includes(q)) {
      score = 50;
      matchedOn = `${spot.area} · ${regionById(spot.region)?.label ?? ""}`;
    } else if (note.includes(q)) {
      score = 60;
      matchedOn = spot.area;
    }

    if (score < Number.POSITIVE_INFINITY) {
      out.push({ spot, matchedOn, score });
    }
  }

  out.sort((a, b) => a.score - b.score || a.spot.name.localeCompare(b.spot.name));
  return out.slice(0, 20);
}
